// src/pages/DreamTeam.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getDocs, getDoc, doc, query, limit } from "firebase/firestore";
import { db } from "../lib/firebase";
import Pitch from "../central/Pitch";
import "../central/PickTeam.css";
import "../central/PitchPages.css";
import { paths } from "../lib/paths";
import { getLeagueConfig } from "../utils/leagueConfig";
import { useSeason } from "../context/SeasonContext";
import PlayerPointsModal from "../components/PlayerPointsModal";
import PageLoader from "../components/PageLoader";
import { selectDreamTeam } from "../utils/dreamTeamUtils";
import { computeMatchResult, RESULT_COLORS } from "../components/matchInput/MatchScoreStep";

// ─── Component ────────────────────────────────────────────────────────────────
export default function DreamTeam() {
  const { leagueID } = useParams();
  const { availableSeasons, selectedSeasonID, currentSeasonID } = useSeason();

  // ── Local season state (isolated — never mutates SeasonContext) ──────────────
  const [pageSeasonID, setPageSeasonID] = useState(selectedSeasonID);
  const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false);
  // true = this is the auto-init run, allow fallback to prior seasons
  // set to false when user explicitly picks a season from the pill
  const isAutoInit = useRef(true);

  // ── View mode & GW selector ───────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState("Total"); // "Total" | "GW"
  const [gameweeks, setGameweeks] = useState([]);     // GW IDs that have results
  const [selectedGW, setSelectedGW] = useState(null);

  // ── Data ──────────────────────────────────────────────────────────────────────
  const [leagueConfig, setLeagueConfig] = useState(null);
  const [players, setPlayers] = useState([]);
  const [pointsMap, setPointsMap] = useState({});    // { playerID: basePoints }
  const [rawPointsMap, setRawPointsMap] = useState({}); // { playerID: full doc }
  const [gwMatchResults, setGwMatchResults] = useState([]); // match docs for selectedGW with both scores present (GW mode only)

  // ── Loading states ─────────────────────────────────────────────────────────────
  const [loadingSetup, setLoadingSetup] = useState(true);   // initial fetch
  const [loadingPoints, setLoadingPoints] = useState(false); // recalculating
  const [noDataAnywhere, setNoDataAnywhere] = useState(false); // true = no GW results in any season

  // League doc's currentGW (e.g. "GW1") — used to detect the S1 GW1 special
  // case, where there's no data yet simply because nothing's been scored.
  const [leagueCurrentGW, setLeagueCurrentGW] = useState(null);

  // ── Modal ──────────────────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPlayerID, setModalPlayerID] = useState(null);
  const [modalPlayerList, setModalPlayerList] = useState([]);
  const [modalIdx, setModalIdx] = useState(0);

  // ── 1. Fetch league config ────────────────────────────────────────────────────
  useEffect(() => {
    if (!leagueID) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "leagues", leagueID));
        if (snap.exists()) {
          const d = snap.data();
          setLeagueConfig(getLeagueConfig(d.leagueType || d.type));
          setLeagueCurrentGW(d.currentGW || null);
        }
      } catch (e) {
        console.error("[DreamTeam] league fetch failed", e);
      }
    })();
  }, [leagueID]);

  // ── 2. Fetch players (season-aware) ──────────────────────────────────────────
  useEffect(() => {
    if (!leagueID || !pageSeasonID) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(paths.leaguePlayers(leagueID, pageSeasonID));
        if (cancelled) return;
        setPlayers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        if (!cancelled) console.error("[DreamTeam] players fetch failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, [leagueID, pageSeasonID]);

  // ── 3. Fetch gameweeks with results (season-aware, with auto-fallback) ─────────
  useEffect(() => {
    if (!leagueID || !pageSeasonID || !availableSeasons.length) return;
    let cancelled = false;
    setLoadingSetup(true);
    setGameweeks([]);
    setSelectedGW(null);
    setNoDataAnywhere(false);

    const findGWsWithData = async (seasonID) => {
      const gwSnap = await getDocs(paths.leagueGameweeks(leagueID, seasonID));
      const gwIds = gwSnap.docs
        .map((d) => d.id)
        .filter((id) => /^GW\d+$/i.test(id))
        .sort((a, b) => (parseInt(a.replace(/\D/g, ""), 10) || 0) - (parseInt(b.replace(/\D/g, ""), 10) || 0));
      const result = [];
      for (const gwID of gwIds) {
        try {
          // Primary signal: playerPoints (GW has been scored/advanced)
          const ptSnap = await getDocs(query(paths.gwPlayerPoints(leagueID, gwID, seasonID), limit(1)));
          if (!ptSnap.empty) { result.push(gwID); continue; }
          // Secondary signal: matches (results entered but GW not yet advanced/scored)
          const matchSnap = await getDocs(query(paths.gwMatches(leagueID, gwID, seasonID), limit(1)));
          if (!matchSnap.empty) result.push(gwID);
        } catch { /* skip */ }
      }
      return result;
    };

    (async () => {
      try {
        let gwsWithData = await findGWsWithData(pageSeasonID);
        if (cancelled) return;

        // Auto-fallback: on initial load, if this season has no results, try prior seasons
        if (gwsWithData.length === 0 && isAutoInit.current) {
          const fallbackSeasons = [...availableSeasons]
            .reverse()
            .filter((s) => s !== pageSeasonID);
          for (const s of fallbackSeasons) {
            const gws = await findGWsWithData(s);
            if (cancelled) return;
            if (gws.length > 0) {
              setPageSeasonID(s); // update pill display; triggers player/points refetch
              setGameweeks(gws);
              setSelectedGW(gws[gws.length - 1]);
              return; // finally still runs
            }
          }
          // No data in any season — hide the page
          if (!cancelled) setNoDataAnywhere(true);
          return;
        }

        setGameweeks(gwsWithData);
        if (gwsWithData.length > 0) setSelectedGW(gwsWithData[gwsWithData.length - 1]);
      } catch (e) {
        if (!cancelled) console.error("[DreamTeam] gameweeks fetch failed", e);
      } finally {
        if (!cancelled) {
          setLoadingSetup(false);
          isAutoInit.current = false; // subsequent season changes are user-initiated
        }
      }
    })();
    return () => { cancelled = true; };
  }, [leagueID, pageSeasonID, availableSeasons]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 4. Compute points (depends on viewMode, selectedGW, gameweeks) ────────────
  useEffect(() => {
    if (!leagueID || !pageSeasonID || gameweeks.length === 0) {
      setPointsMap({});
      setRawPointsMap({});
      return;
    }
    if (viewMode === "GW" && !selectedGW) return;

    let cancelled = false;
    setLoadingPoints(true);

    (async () => {
      try {
        const newRaw = {};

        if (viewMode === "GW") {
          const ptSnap = await getDocs(paths.gwPlayerPoints(leagueID, selectedGW, pageSeasonID));
          ptSnap.docs.forEach((d) => {
            newRaw[d.id] = { id: d.id, ...d.data() };
          });
        } else {
          // Total: sum base points across all GWs with data
          for (const gwID of gameweeks) {
            const ptSnap = await getDocs(paths.gwPlayerPoints(leagueID, gwID, pageSeasonID));
            ptSnap.docs.forEach((d) => {
              const data = d.data();
              const base = Number(data.total ?? data.totalPoints ?? 0);
              if (!newRaw[d.id]) {
                newRaw[d.id] = { id: d.id, total: 0 };
              }
              newRaw[d.id].total = (newRaw[d.id].total || 0) + base;
            });
          }
        }

        if (cancelled) return;

        setRawPointsMap(newRaw);

        const pMap = {};
        Object.entries(newRaw).forEach(([pid, rec]) => {
          pMap[pid] = Number(rec.total ?? rec.totalPoints ?? 0);
        });
        setPointsMap(pMap);
      } catch (e) {
        if (cancelled) return;
        console.error("[DreamTeam] points fetch failed", e);
        setPointsMap({});
        setRawPointsMap({});
      } finally {
        if (!cancelled) setLoadingPoints(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leagueID, pageSeasonID, viewMode, selectedGW, gameweeks]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 4b. Fetch match results for selectedGW — GW mode only (Total mode aggregates
  //         across gameweeks, so a single match result doesn't apply there) ────────
  useEffect(() => {
    if (viewMode !== "GW" || !leagueID || !pageSeasonID || !selectedGW) {
      setGwMatchResults([]);
      return;
    }
    let isMounted = true;
    (async () => {
      try {
        const matchSnap = await getDocs(paths.gwMatches(leagueID, selectedGW, pageSeasonID));
        const matchesWithScores = matchSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((m) => m?.yourScore !== undefined && m?.yourScore !== null && m?.theirScore !== undefined && m?.theirScore !== null);
        if (isMounted) setGwMatchResults(matchesWithScores);
      } catch (e) {
        console.error("[DreamTeam] match results fetch failed", e);
        if (isMounted) setGwMatchResults([]);
      }
    })();
    return () => { isMounted = false; };
  }, [leagueID, pageSeasonID, viewMode, selectedGW]);

  // ── Dream team selection (memoised) ──────────────────────────────────────────
  const dreamTeam = useMemo(() => {
    if (!leagueConfig?.squadConfig || players.length === 0 || Object.keys(pointsMap).length === 0)
      return null;
    return selectDreamTeam(players, pointsMap, leagueConfig.squadConfig);
  }, [leagueConfig, players, pointsMap]);

  // ── Pitch helpers ─────────────────────────────────────────────────────────────
  const positions = useMemo(
    () => leagueConfig?.squadConfig?.positions || ["DEF", "MID", "FWD"],
    [leagueConfig]
  );

  const playersById = useMemo(() => {
    const map = new Map();
    players.forEach((p) => map.set(p.id, { ...p, name: p.name || p.id }));
    return map;
  }, [players]);

  const pitchPointsById = useMemo(() => {
    if (!dreamTeam) return {};
    const result = {};
    Object.values(dreamTeam)
      .flat()
      .forEach((pid) => { result[pid] = pointsMap[pid] || 0; });
    return result;
  }, [dreamTeam, pointsMap]);

  // ── Modal helpers ─────────────────────────────────────────────────────────────
  const openModal = (playerID) => {
    if (!dreamTeam) return;
    const allPlayers = positions.flatMap((pos) => dreamTeam[pos] || []);
    const idx = allPlayers.indexOf(playerID);
    setModalPlayerList(allPlayers);
    setModalIdx(idx >= 0 ? idx : 0);
    setModalPlayerID(playerID);
    setModalOpen(true);
  };

  const activePlayer = useMemo(
    () => players.find((p) => p.id === modalPlayerID) || null,
    [modalPlayerID, players]
  );

  const activePlayerGWPoints = useMemo(
    () => (modalPlayerID ? rawPointsMap[modalPlayerID] || null : null),
    [modalPlayerID, rawPointsMap]
  );

  const canPrevModal = modalIdx > 0;
  const canNextModal = modalIdx < modalPlayerList.length - 1;

  const goToPrevModal = () => {
    if (!canPrevModal) return;
    const idx = modalIdx - 1;
    setModalIdx(idx);
    setModalPlayerID(modalPlayerList[idx]);
  };

  const goToNextModal = () => {
    if (!canNextModal) return;
    const idx = modalIdx + 1;
    setModalIdx(idx);
    setModalPlayerID(modalPlayerList[idx]);
  };

  // ── GW navigator ──────────────────────────────────────────────────────────────
  const gwIdx = gameweeks.indexOf(selectedGW);
  const canPrevGW = gwIdx > 0;
  const canNextGW = gwIdx >= 0 && gwIdx < gameweeks.length - 1;

  const arrowBtn = (enabled) => ({
    background: "none",
    border: "none",
    cursor: enabled ? "pointer" : "default",
    padding: "0.3rem 0.6rem",
    fontSize: "1rem",
    color: enabled ? "#50B4C8" : "#ccc",
    fontWeight: "700",
    lineHeight: 1,
  });

  // ── Early states ──────────────────────────────────────────────────────────────
  if (loadingSetup) return <PageLoader message="Loading Dream Team…" />;

  // S1 GW1 specifically — there's no data yet because nothing has been
  // scored this early in the season, not because of a data problem.
  // (currentSeasonID here comes from SeasonContext, which itself reads the
  // same league doc's currentSeasonID field — no need to refetch it.)
  if (currentSeasonID === "S1" && leagueCurrentGW === "GW1") {
    return (
      <p style={{ textAlign: "center", marginTop: "2rem", color: "#999" }}>
        Dream Team unlocks once GW1 results are scored. Check back for GW2.
      </p>
    );
  }

  // No results in any season — hide the page entirely
  if (noDataAnywhere) return null;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <>
      <div style={{ width: "100%", maxWidth: "420px", margin: "0 auto", padding: "0.75rem 0.75rem 0", boxSizing: "border-box" }}>

        {/* Page title */}
        <h2 style={{ textAlign: "center", fontSize: "1.25rem", fontWeight: "700", margin: "0 0 0.75rem", color: "#111" }}>
          Dream Team
        </h2>

        {/* Season selector pill — local state only, never touches SeasonContext */}
        {availableSeasons.length > 1 && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.65rem", position: "relative" }}>
            <div style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => setSeasonDropdownOpen((v) => !v)}
                style={{
                  border: "1px solid #d9e0e6",
                  borderRadius: "999px",
                  padding: "0.22rem 0.65rem",
                  background: "#f4f7f8",
                  fontSize: "0.78rem",
                  fontWeight: "600",
                  color: "#555",
                  cursor: "pointer",
                  lineHeight: 1.4,
                }}
              >
                {pageSeasonID} ▾
              </button>
              {seasonDropdownOpen && (
                <div style={{
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  top: "calc(100% + 4px)",
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "0.35rem",
                  zIndex: 20,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  minWidth: "72px",
                }}>
                  {availableSeasons.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => { isAutoInit.current = false; setPageSeasonID(s); setSeasonDropdownOpen(false); }}
                      style={{
                        display: "block",
                        width: "100%",
                        background: s === pageSeasonID ? "#50B4C8" : "transparent",
                        color: s === pageSeasonID ? "#fff" : "#234",
                        border: "none",
                        borderRadius: "8px",
                        padding: "0.3rem 0.6rem",
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        textAlign: "center",
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {pageSeasonID !== currentSeasonID && (
              <span style={{ fontSize: "0.75rem", color: "#999", alignSelf: "center", marginLeft: "0.5rem" }}>
                Viewing {pageSeasonID}
              </span>
            )}
          </div>
        )}

        {/* Total / GW toggle */}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.5rem", marginBottom: "0.65rem" }}>
          {["Total", "GW"].map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "999px",
                padding: "0.3rem 0.85rem",
                fontSize: "0.85rem",
                fontWeight: "600",
                background: viewMode === mode ? "#50B4C8" : "#f4f7f8",
                color: viewMode === mode ? "#fff" : "#222",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {mode}
            </button>
          ))}

          {/* GW arrow pill — only shown in GW mode */}
          {viewMode === "GW" && (
            <div style={{ display: "inline-flex", alignItems: "center", background: "#f4f7f8", borderRadius: "999px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
              <button
                style={arrowBtn(canPrevGW)}
                disabled={!canPrevGW}
                onClick={() => canPrevGW && setSelectedGW(gameweeks[gwIdx - 1])}
                aria-label="Previous gameweek"
              >←</button>
              <span style={{ fontWeight: 600, fontSize: "0.85rem", minWidth: 44, textAlign: "center", color: "#222", padding: "0 0.1rem" }}>
                {selectedGW || "—"}
              </span>
              <button
                style={arrowBtn(canNextGW)}
                disabled={!canNextGW}
                onClick={() => canNextGW && setSelectedGW(gameweeks[gwIdx + 1])}
                aria-label="Next gameweek"
              >→</button>
            </div>
          )}
        </div>

        {/* Match result pills — GW mode only, only when scores were recorded */}
        {viewMode === "GW" && gwMatchResults.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "0.4rem", marginBottom: "0.65rem" }}>
            {gwMatchResults.map((m) => {
              const resultValue = m.result || computeMatchResult(m.yourScore, m.theirScore);
              const color = resultValue ? RESULT_COLORS[resultValue] : "#50B4C8";
              const opponentLabel = m.opponent && String(m.opponent).trim() !== "" ? m.opponent : "Opponent";
              return (
                <div
                  key={m.id}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "0.3rem 0.85rem", background: `${color}1A`, border: `1px solid ${color}`,
                    borderRadius: "999px", fontSize: "0.82rem", color, fontWeight: 600, whiteSpace: "nowrap",
                  }}
                >
                  vs {opponentLabel}: {m.yourScore}–{m.theirScore}
                </div>
              );
            })}
          </div>
        )}

        {/* Loading indicator for points recalculation */}
        {loadingPoints && (
          <p style={{ textAlign: "center", fontSize: "0.82rem", color: "#999", margin: "0 0 0.5rem" }}>
            Calculating…
          </p>
        )}

        {/* Pitch */}
        {!loadingPoints && dreamTeam ? (
          <div className="pitch-panel">
            <Pitch
              team={dreamTeam}
              playersById={playersById}
              positions={positions}
              pointsById={pitchPointsById}
              valueSuffix=" pts"
              onPlayerClick={openModal}
            />
          </div>
        ) : !loadingPoints && (
          <p style={{ textAlign: "center", fontSize: "0.88rem", color: "#999", marginTop: "2rem" }}>
            No points data available.
          </p>
        )}
      </div>

      {/* Player points modal */}
      <PlayerPointsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        player={activePlayer}
        leagueID={leagueID}
        initialSeasonID={pageSeasonID}
        onPrev={goToPrevModal}
        onNext={goToNextModal}
        canPrev={canPrevModal}
        canNext={canNextModal}
        currentGameweek={viewMode === "GW" ? selectedGW : pageSeasonID}
        gwLabel={viewMode === "GW" ? selectedGW : `${pageSeasonID} Total`}
        playerGWPoints={activePlayerGWPoints ? {
          ...activePlayerGWPoints,
          total: pointsMap[modalPlayerID] || 0,
        } : null}
        breakdownRows={[]}
        isCaptain={false}
        isSelected={false}
      />
    </>
  );
}
